import React, { useState, useEffect } from 'react';
import {
  Flex,
  Typography,
  Button,
  Card,
  Table,
  Tag,
  Avatar,
  Modal,
  Form,
  Input,
  Select,
  Spin,
  Space,
  Row,
  Col,
  Pagination
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BankOutlined,
  TeamOutlined,
  UserOutlined,
  ReloadOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../../i18n/TranslationContext';
import { useNotification } from '../../hooks/useNotification';
import DashboardService from '../../service';
import { handleApiResponse, handleApiError } from '../../utils/apiResponseHandler';
import CreatorInfo from '../../components/CreatorInfo';

const BranchesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { lang } = useParams();
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning } = useNotification();
  const [branches, setBranches] = useState([]);
  const [branchStats, setBranchStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [form] = Form.useForm();
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    city: '',
    location: '',
    flag: '',
    code: '',
    address: '',
    phone: '',
    email: '',
    status: 'active'
  });

  useEffect(() => {
    loadBranches(1);
    loadBranchStats();
  }, []);

  const loadBranches = async (page = 1) => {
    try {
      setLoading(true);
      const data = await DashboardService.getBranches(page, 100);
      if (data.branches) {
        setBranches(data.branches);
      }
      if (data.pagination) {
        setPagination(data.pagination);
        setCurrentPage(data.pagination.currentPage);
      }
    } catch (error) {
      console.error('Failed to load branches:', error);
      showError('Failed to load branches. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadBranches(page);
  };

  const loadBranchStats = async () => {
    try {
      const data = await DashboardService.getDealersByBranch();
      if (data.success) {
        setBranchStats(data);
      }
    } catch (error) {
      console.error('Failed to load branch statistics:', error);
    }
  };

  const handleCreate = () => {
    setEditingBranch(null);
    setFormData({
      name: '',
      country: '',
      city: '',
      location: '',
      flag: '',
      code: '',
      address: '',
      phone: '',
      email: '',
      status: 'active'
    });
    form.resetFields();
    setIsFormOpen(true);
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setFormData({
      name: branch.name,
      country: branch.country,
      city: branch.city,
      location: branch.location,
      flag: branch.flag,
      code: branch.code,
      address: branch.address,
      phone: branch.phone,
      email: branch.email,
      status: branch.status
    });
    form.setFieldsValue({
      name: branch.name,
      country: branch.country,
      city: branch.city,
      location: branch.location,
      flag: branch.flag,
      code: branch.code,
      address: branch.address,
      phone: branch.phone,
      email: branch.email,
      status: branch.status
    });
    setIsFormOpen(true);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete branch "${name}"?`)) {
      return;
    }

    try {
      const result = await DashboardService.deleteBranch(id);
      handleApiResponse(result, { showSuccess, showError }, {
        successMessage: 'Branch deleted successfully!',
        errorMessage: 'Failed to delete branch',
        onSuccess: () => {
          loadBranches(1);
          loadBranchStats();
        }
      });
    } catch (error) {
      handleApiError(error, { showError }, 'Failed to delete branch. Please try again.');
    }
  };

  const handleFormSubmit = async (values) => {
    try {
      const formDataToSend = { ...values, location: `${values.city || ''}, ${values.country || ''}` };
      if (editingBranch) {
        const result = await DashboardService.updateBranch(editingBranch.id, formDataToSend);
        handleApiResponse(result, { showSuccess, showError }, {
          successMessage: 'Branch updated successfully!',
          errorMessage: 'Failed to update branch',
          onSuccess: () => {
            loadBranches(currentPage);
            loadBranchStats();
            setIsFormOpen(false);
            setEditingBranch(null);
          }
        });
      } else {
        const branchData = {
          ...formDataToSend,
          location: `${formDataToSend.city}, ${formDataToSend.country}`,
        };
        const result = await DashboardService.createBranch(branchData);
        handleApiResponse(result, { showSuccess, showError }, {
          successMessage: 'Branch created successfully!',
          errorMessage: 'Failed to create branch',
          onSuccess: () => {
            loadBranches(1);
            loadBranchStats();
            setIsFormOpen(false);
          }
        });
      }
    } catch (error) {
      handleApiError(error, { showError }, 'Failed to save branch. Please try again.');
    }
  };

  const handleBranchClick = (branchId) => {
    navigate(`/${lang}/branches/${branchId}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const stats = [
    {
      title: t('branches.totalBranches'),
      value: branchStats?.totals?.totalBranches || branches.length,
      icon: <BankOutlined />,
      color: '#1e3a5f'
    },
    {
      title: t('branches.activeBranches'),
      value: branches.filter(b => b.status === 'active').length,
      icon: <BankOutlined />,
      color: '#22c55e'
    },
    {
      title: t('branches.totalManagers'),
      value: branchStats?.totals?.totalManagers || branches.reduce((sum, b) => sum + (b.managerCount || 0), 0),
      icon: <UserOutlined />,
      color: '#f59e0b'
    },
    {
      title: t('dealers.totalDealers'),
      value: branchStats?.totals?.totalDealers || 0,
      icon: <TeamOutlined />,
      color: '#8b5cf6'
    },
    {
      title: t('branches.totalSubdealers'),
      value: branchStats?.totals?.totalSubdealers || branches.reduce((sum, b) => sum + (b.subdealerCount || 0), 0),
      icon: <TeamOutlined />,
      color: '#6366f1'
    }
  ];

  const columns = [
    {
      title: 'Branch',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => (
        <Flex align="center" gap={8}>
          <Avatar style={{ backgroundColor: '#1e3a5f' }}>{record.flag}</Avatar>
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>{record.city}</Typography.Text>
          </div>
        </Flex>
      )
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <EnvironmentOutlined />
          {record.location}
        </Flex>
      )
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (code) => <Tag>{code}</Tag>
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div>{record.phone}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Typography.Text>
        </div>
      )
    },
    {
      title: 'Managers',
      key: 'managers',
      render: (_, record) => {
        const branchStat = branchStats?.branches?.find(bs => bs.branchId === record.id);
        const count = branchStat?.managerCount ?? record.managerCount ?? 0;
        return <Tag icon={<UserOutlined />} color="blue">{count}</Tag>;
      }
    },
    {
      title: 'Dealers',
      key: 'dealers',
      render: (_, record) => {
        const branchStat = branchStats?.branches?.find(bs => bs.branchId === record.id);
        const count = branchStat?.dealerCount ?? record.dealerCount ?? 0;
        return <Tag icon={<TeamOutlined />} color="cyan">{count}</Tag>;
      }
    },
    {
      title: 'Subdealers',
      key: 'subdealers',
      render: (_, record) => {
        const branchStat = branchStats?.branches?.find(bs => bs.branchId === record.id);
        const count = branchStat?.subdealerCount ?? record.subdealerCount ?? 0;
        return <Tag icon={<TeamOutlined />}>{count}</Tag>;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => date ? new Date(date).toLocaleDateString() : '-'
    },
    {
      title: 'Created By',
      key: 'createdBy',
      render: (_, record) => (
        record.createdBy ? (
          <CreatorInfo createdBy={record.createdBy} createdAt={record.createdAt} variant="compact" />
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        )
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit Branch"
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(record.id, record.name);
            }}
            title="Delete Branch"
          />
        </Space>
      )
    }
  ];

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>{t('branches.title')}</Typography.Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => loadBranches(currentPage)} title={t('common.refresh')} />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('branches.addBranch')}
          </Button>
        </Space>
      </Flex>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {stats.map((stat, index) => (
          <Col xs={24} sm={12} md={8} lg={6} key={index}>
            <Card size="small">
              <Flex align="center" gap={8}>
                <span style={{ color: stat.color, fontSize: 20 }}>{stat.icon}</span>
                <div>
                  <Typography.Title level={4} style={{ margin: 0 }}>{stat.value}</Typography.Title>
                  <Typography.Text type="secondary">{stat.title}</Typography.Text>
                </div>
              </Flex>
            </Card>
          </Col>
        ))}
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={branches}
          rowKey="id"
          pagination={false}
          onRow={(record) => ({
            onClick: () => handleBranchClick(record.id),
            style: { cursor: 'pointer' }
          })}
          locale={{ emptyText: 'No branches found' }}
        />
      </Card>

      {pagination.totalPages > 1 && (
        <Flex justify="center" align="center" gap={16} style={{ marginTop: 24 }}>
          <Pagination
            total={pagination.totalUsers ?? branches.length}
            current={currentPage}
            pageSize={100}
            onChange={handlePageChange}
            showSizeChanger={false}
            showTotal={(total) => `Showing ${branches.length} of ${total} total`}
          />
        </Flex>
      )}

      <Modal
        title={editingBranch ? t('branches.editBranch') : t('branches.createBranch')}
        open={isFormOpen}
        onCancel={() => setIsFormOpen(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          initialValues={formData}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Branch Name" rules={[{ required: true }]}>
                <Input placeholder="e.g., Georgia Branch" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="code" label="Branch Code" rules={[{ required: true }]}>
                <Input placeholder="e.g., GE-TBS" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="country" label="Country" rules={[{ required: true }]}>
                <Input placeholder="e.g., Georgia" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="city" label="City" rules={[{ required: true }]}>
                <Input placeholder="e.g., Tbilisi" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="flag" label="Flag Emoji">
                <Input placeholder="e.g., 🇬🇪" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status">
                <Select
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                    { value: 'suspended', label: 'Suspended' }
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="address" label="Address">
                <Input placeholder="Full address" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="Phone">
                <Input placeholder="+995 32 123 4567" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="email" label="Email">
                <Input type="email" placeholder="branch@bidmanager.com" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space>
              <Button onClick={() => setIsFormOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {editingBranch ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BranchesPage;
